import { Language } from './location';
import { UserData } from './user';
import { EventStatus, Location } from './event';

export type Category = {
  name: string;
  nameEnglish: string | null;
  id: number;
  default: boolean;
};

export type GuestData = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  language: string;
  guestTicketComment?: string;
};

export interface CreateGuestInvitationItemDto {
  ticketTypeId: number;
  ticketTypeName?: string;
  quantity: number;
  standingId?: number;
  seatId?: number;
}

export interface CreateGuestsDto extends GuestData {
  guestInvitationItems: CreateGuestInvitationItemDto[];
}

export interface CreateGuestInvitationDto {
  guestCategoryId: number;
  guestCategoryName?: string;
  partnerUserId?: number;
  guests: CreateGuestsDto[];
  sendingEmail?: boolean;
}

export interface GuestInvitationItem extends CreateGuestInvitationItemDto {
  ticketTypeName: string;
  ticketTypeNameEnglish: string | null;
}

export type GuestFormValuesProps = {
  guestCategoryId: number;
  guestInvitationItems: CreateGuestInvitationItemDto[];
  guests: GuestData[];
};

export type UploadedGuest = {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email: string;
  guestCategory: string;
  ticketType: string;
  quantity: number;
  language: Language;
  guestTicketComment?: string;
};

export type UploadedGuestWithRowNum = UploadedGuest & {
  __rowNum__: number;
};

export type EventGuestStatistics = {
  eventId: number;
  eventName: string;
  eventEnglishName: string | null;
  eventLocation: string;
  eventStartDate: string;
  groups: Groups[];
  summary: number;
};

export type Groups = {
  groupId: number;
  groupName: string;
  groupEnglishName: string | null;
  groupItems: GroupItem[];
  summary: number;
};

export type GroupItem = {
  groupItemId: number;
  groupItemName: string;
  groupItemEnglishName: string | null;
  summary: number;
};

export type GuestInvitationWithRelatedData = {
  id: number;
  date: string;
  firstName: string;
  lastName: string;
  email: string;
  event: {
    id: number;
    name: string;
    nameEnglish: string;
    date: string;
    startingTime: string;
  };
  partnerUser: {
    id: number;
    firstName: string;
    lastName: string;
  };
  guestInvitationItems: [
    {
      id: number;
      quantity: number;
      ticketTypeId: number;
      ticketTypeName: string;
      ticketTypeEnglishName: string;
    },
  ];
};

export type GuestTicketWithTicketTypeName = {
  id: number;
  barcode: number;
  link: string;
  sent: boolean;
  seatName: string | null;
  rowName: string | null;
  sectionName: string | null;
  sectionNameEnglish: string | null;
  ticketTypeName: string;
  ticketTypeEnglishName: string;
};

export type GuestDetailsWithRelatedData = {
  id: number;
  partnerUserId: number;
  eventId: number;
  guestCategoryId: number;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  email: string;
  language: Language;
  guestTicketComment: string;
  date: string;
  partnerUser: {
    firstName: string;
    lastName: string;
  };
  event: {
    name: string;
    nameEnglish: string;
    date: string;
    startingTime: string;
    hasSeatMap: boolean;
  };
  guestCategory: {
    name: string;
    nameEnglish: string | null;
  };
};

export enum GuestHistoryAction {
  ALL = 'all',
  MODIFY_EMAIL = 'modifyEmail',
  REGENERATE = 'regenerate',
  RESEND = 'resend',
  DELETE = 'delete',
}

export type GuestHistory = {
  id: number;
  partnerUserId: number;
  partnerUser: {
    firstName: string;
    lastName: string;
  };
  action: GuestHistoryAction;
  date: string;
  details: string;
};

export type GuestSettingsResponse = {
  partnerUserId: number;
  organizerId: number;
  replyTo: string;
  emailContent: string;
  emailContentEnglish: string;
  replyToConfirmed: boolean;
  defaultEmailContent?: string | null;
  defaultEmailContentEnglish?: string | null;
};

export type UserWithGuestLimit = UserData & {
  guestLimit?: number;
};

export type TicketTypeWithGuestLimitData = {
  id: number;
  name: string;
  nameEnglish: string;
  guestLimit?: number;
  sentGuestTickets: number;
  freeGuestTickets: number | null;
};

export type EventWithGuestData = {
  id: number;
  name: string;
  nameEnglish: string;
  startingTime: string;
  salesStartDate: string;
  salesEndDate: string;
  closingTime: string;
  status: EventStatus;
  seatMapId: number;
  guestLimit?: number | null;
  guestCount?: number;
  location: Location;
  ticketTypes: TicketTypeWithGuestLimitData[];
};

export type GuestUploadSummaryData = {
  guestCategory: string;
  ticketType: string;
  quantity: number;
  guestCategoryId?: number;
  ticketTypeId?: number;
};

export type GuestUploadLimitSummary = Record<
  number,
  { id: number; guestLimit?: number; sentGuestTickets: number; freeGuestTickets: number | null }
>;

export enum UploadedGuestContentType {
  STRING = 'string',
  NUMBER = 'number',
  EMAIL = 'email',
  LANGUAGE = 'language',
}

export enum DynamicContentHu {
  SURNAME = '#vezetéknév',
  FIRST_NAME = '#keresztnév',
  ORGANIEZR = '#szervező',
  EVENT = '#rendezvény',
  CATEGORY = '#kategória',
}

export enum DynamicContentEn {
  SURNAME = '#surname',
  FIRST_NAME = '#firstname',
  ORGANIEZR = '#organizer',
  EVENT = '#event',
  CATEGORY = '#category',
}

export enum GuestSettingsLevel {
  USER = 'user',
  ORGANIZER = 'organizer',
}

export enum EventGuestsSearchType {
  INVITATION = 'invitation',
  LIMIT = 'limit',
}

export enum CreateGuestInvitationType {
  INVITE = 'invite',
  GENERATE = 'generate',
}
